import React from "react";

/**
 * Top-level error boundary. React 18 propagates render-time exceptions all
 * the way to the root — if nothing catches them the entire tree unmounts
 * and the user sees a blank white page with no explanation.
 *
 * This boundary:
 *   - Logs the error + componentStack to the console for debugging.
 *   - Renders a friendly fallback with a one-click "Reload" so the user
 *     can escape without opening devtools.
 *   - Keeps the brand look (dark background, orange accent) consistent.
 *
 * The boundary is wrapped around the full `<App />` tree in `App.js`.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to the console so dev tools reveal the stack. We purposefully
    // avoid shipping to a remote log sink here — the top-level boundary
    // should NEVER fail-closed on a network call, that'd defeat the point.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Render crash:", error, info?.componentStack);
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* no-op */ }
  };

  handleGoHome = () => {
    try { window.location.href = "/"; } catch { /* no-op */ }
  };

  render() {
    if (!this.state.error) return this.props.children;
    const message = String(this.state.error?.message || this.state.error || "Unknown error");
    return (
      <div
        className="min-h-screen bg-[#050505] text-zinc-100 flex items-center justify-center p-6"
        data-testid="root-error-boundary"
      >
        <div className="max-w-md w-full border border-zinc-800 rounded-xl p-6 space-y-4 bg-zinc-950/60">
          <div className="flex items-center gap-2 text-[#FF4500]">
            <span className="inline-block w-2 h-2 rounded-full bg-[#FF4500] animate-pulse" />
            <span className="text-xs uppercase tracking-wider">Something broke</span>
          </div>
          <h1 className="text-xl font-semibold">We hit an unexpected error loading this page.</h1>
          <p className="text-sm text-zinc-400">
            It's safe to reload — your data is untouched. If this keeps happening, tell
            support what you were doing just before the screen went blank.
          </p>
          <details className="text-xs text-zinc-500">
            <summary className="cursor-pointer hover:text-zinc-300">Error details</summary>
            <pre className="mt-2 whitespace-pre-wrap break-words text-[11px] bg-black/50 border border-zinc-900 rounded p-2 max-h-48 overflow-auto">
              {message}
            </pre>
          </details>
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="flex-1 bg-[#FF4500] hover:bg-[#FF6A33] text-black font-semibold rounded-full h-10 text-sm transition-colors"
              data-testid="error-boundary-reload-btn"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              className="flex-1 border border-zinc-700 hover:border-zinc-500 text-zinc-200 rounded-full h-10 text-sm transition-colors"
              data-testid="error-boundary-home-btn"
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
